import {head, last, capitalize} from 'lodash'
import { bearing, center, length, lineSliceAlong, lineString, point, points, transformTranslate, angle } from '@turf/turf'
import { getAverageCardinalDirection, lineOffsetPerpendicular } from '@shared/utils/geometry'

import { PRIORITY_ENUM, TURN_ENUM } from '@shared/constants'
import {minimizeID, maximizeID} from '@shared/utils/uuid-compression'


const MAX_ARROWSET_LENGTH_METERS = 7
const MIN_ARROWSET_LENGTH_METERS = 5
const ARROWSET_DISTANCE_FROM_STOP_BAR_METERS = -2 // Positive = into intersection; Negative = away from intersection
const ARROW_TURNED_BODY_LENGTH_TO_TOTAL_LENGTH_RATIO = .3
const TURNING_ARROW_LENGTH_TO_THROUGH_ARROW_LENGTH_RATIO = .8

const ARROW_TOTAL_WIDTH_METERS = 1
const ARROW_MARGIN_METERS = .15
const ARROW_BODY_WIDTH_METERS = ARROW_TOTAL_WIDTH_METERS - (ARROW_MARGIN_METERS * 2)

const LANE_ARROWSET_GAP_METERS = 5

const MARKER_ANCHOR_DIRECTIONS = ['top', 'top-right', 'right', 'bottom-right', 'bottom', 'bottom-left', 'left', 'top-left']


export const getEdgeConnectionNames = ({edge_connection_id, edge_connections, abbreviate_turns}) => {
	const labeled_edge_connections = edge_connections?.map(ec => {

		const direction_name = (abbreviate_turns) ? `${ec.start_edge?.cardinal_direction ?? getAverageCardinalDirection(ec.start_edge?.gps, {include_ordinal_directions: true})}${ec.turn[0].toUpperCase()}`
			: `${ec.start_edge?.cardinal_direction ?? getAverageCardinalDirection(ec.start_edge?.gps, {include_ordinal_directions: true})} ${capitalize(ec.turn)}`

		const start_edge_disambiguation = (edge_connections.some(other_ec => other_ec.id !== ec.id && other_ec.end_edge_id === ec.end_edge_id && other_ec.turn === ec.turn)) ? `from ${ec?.start_edge?.road_name}`: ''
		const end_edge_disambiguation = (edge_connections.some(other_ec => other_ec.id !== ec.id && other_ec.start_edge_id === ec.start_edge_id && other_ec.turn === ec.turn)) ? `to ${ec?.end_edge?.road_name}`: ''

		const formatted_edge_disambiguation = (!start_edge_disambiguation && !end_edge_disambiguation) ? ''
			: (!start_edge_disambiguation || !end_edge_disambiguation) ? ` (${start_edge_disambiguation || end_edge_disambiguation})`
			: ` (${start_edge_disambiguation} ${end_edge_disambiguation})`

		return {
			...ec,
			name: `${direction_name}${formatted_edge_disambiguation}`
		}

	})

	return (edge_connection_id) ? labeled_edge_connections?.find(ec => ec.id === edge_connection_id)?.name
		: labeled_edge_connections

}

export const sortByDirectionAndTurn = (a, b) => {
	// Sort alphabetically by name
	return (a.name || '').localeCompare(b.name || '')
}

export const getPhaseApproachMovementName = ({
	phase_id,
	phase_type_id,
	approach_id,
	movement_id,
	atomic_movement_id,

	phases,
	edge_connections,
	intersections,
	movements,
	atomic_movements,
}) => {

	if (edge_connections.some(ec => !ec?.movement_ids || !ec?.atomic_movement_ids)) {
		console.error('getPhaseApproachMovementName: Invalid Connection detected while generating signalized approach arrowset - Missing Movement IDs.')
		return [
			'All Phases',
			'All Phase Types',
			'All Approaches',
			'All Movements',
		]
	}

	const _movements = [].concat(
		(Array.isArray(movements) ? movements : [movements].filter(x => x)).map(m => ({ ...m, is_atomic_movement: false })),
		(Array.isArray(atomic_movements) ? atomic_movements : [atomic_movements].filter(x => x)).map(m => ({ ...m, is_atomic_movement: true })),
	)

	const approaches = edge_connections.reduce((approaches, ec) => {

		if (!ec.start_edge_id || approaches.some(a => a.id === ec.start_edge_id)) return approaches

		const intersection = intersections.find(i => i.id === ec?.intersection_id)
		const intersection_disambiguation = (edge_connections.some(ec2 => ec2.intersection_id !== ec.intersection_id && ec2?.start_edge?.road_name === ec?.start_edge?.road_name)) ? ` - ${intersection?.name || intersection?.name_short}` : ''

		return [
			...approaches,
			{
				id: ec?.start_edge_id,
				intersection_id: ec?.intersection_id,
				approach_name: ec?.start_edge?.road_name,
				intersection_name: intersection?.name_short || intersection?.name,
				name: `${ec?.start_edge?.cardinal_direction}${intersection_disambiguation}`,
			},
		]

	}, [])

	const labeled_movements = _movements.map(m => {
		let movement_label = null //m?.name
		if (!movement_label) {
			// if (m?.overlap_number != null) {
			// 	movement_label = `Overlap ${'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.at(m.overlap_number-1)}`
			// }
			// else {
				const edge_connections_for_movement = edge_connections.filter(ec => (m.is_atomic_movement) ? ec.atomic_movement_ids.includes(m.id) : ec.movement_ids.includes(m.id))
				const left_turns = edge_connections_for_movement.filter(ec => ec.turn === TURN_ENUM.left)
				const throughs = edge_connections_for_movement.filter(ec => ec.turn === TURN_ENUM.through)
				const right_turns = edge_connections_for_movement.filter(ec => ec.turn === TURN_ENUM.right)

				const turns_label = [left_turns.length && 'Left', throughs.length && 'Thru', right_turns.length && 'Right'].filter(x => x).join('-')

				// Show end_edge name if there's another edge_connection with same approach and turn type to disambiguate
				const other_left_turn_exists = edge_connections.some(ec2 => left_turns.length && ec2.turn === TURN_ENUM.left && left_turns.some(ec => ec.start_edge_id === ec2.start_edge_id && ec.id !== ec2.id))
				const other_through_exists = edge_connections.some(ec2 => throughs.length && ec2.turn === TURN_ENUM.through && throughs.some(ec => ec.start_edge_id === ec2.start_edge_id && ec.id !== ec2.id))
				const other_right_turn_exists = edge_connections.some(ec2 => right_turns.length && ec2.turn === TURN_ENUM.right && right_turns.some(ec => ec.start_edge_id === ec2.start_edge_id && ec.id !== ec2.id))

				let end_edge_disambiguation = ''
				if (other_left_turn_exists || other_through_exists || other_right_turn_exists) {
					const sorted_end_edge_names = edge_connections_for_movement
						.toSorted((a,b) => {
							if (a.turn === TURN_ENUM.left && b.turn !== TURN_ENUM.left) return -1
							if (a.turn === TURN_ENUM.through && (b.turn !== TURN_ENUM.left || b.turn !== TURN_ENUM.through)) return -1
							if (a.turn === b.turn && (a.end_edge?.road_name?.toLowerCase() ?? 'a') < (b.end_edge?.road_name?.toLowerCase() ?? 'a')) return -1
							return 1
						})
						.map(ec => `${ec.end_edge?.cardinal_direction || ''} ${ec.end_edge?.road_name || ''}`.trim() || '--')
					end_edge_disambiguation = ` (to ${sorted_end_edge_names.join('|')})`
				}

				// movement_label = `${(m.is_atomic_movement) ? '	' : ''}${capitalize(m.priority)} ${turns_label}${end_edge_disambiguation}`
				movement_label = `${turns_label}${end_edge_disambiguation}`

			// }
		}

		return {
			...m,
			name: movement_label,
			is_overlap: m.overlap_number != null,
			parent_movement_id: (m.is_atomic_movement) ? m.movement_id : undefined,
		}
	})


	const phase = (phase_id != null) ? phases?.find(p => p.id === phase_id) : null
	const phase_name = (phase) ? (phase?.name ?? `Phase ${phase?.number}`) : 'All Phases'

	const phase_type_name = (Object.keys(PRIORITY_ENUM).includes(phase_type_id)) ? capitalize(phase_type_id)
		: (phase_type_id && phase_type_id.startsWith('overlap_') && phase_type_id.split('_').length === 2) ? `Overlap ${'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.at(phase_type_id.split('_')[1]-1)}`
		: 'All Phase Types'

	const approach_name = (approach_id != null) ? approaches?.find(a => a.id === approach_id)?.name : 'All Approaches'

	const movement_name = (movement_id || atomic_movement_id) ? labeled_movements?.find(m => m.id === movement_id || m.id === atomic_movement_id)?.name : 'All Movements'


	return [
		phase_name,
		phase_type_name,
		approach_name,
		movement_name,
	]

}

export const generateArrowGeometry =({
	turn,
	arrowset_linestring,
	offset,
	point_towards_coordinates,
	should_add_stop_bar_padding,
}) => {

	const arrowset_linestring_length = length(arrowset_linestring, { units: 'meters' })

	// An arrow shape consists of 3 parts:
	// 1. A main body that follows the arrow's offset'd arrowset_linestring
	// 2. A turned body that points from the end of the main body to...
	//     - for edge_connections that are unique for their turn type:
	//         - straight or 90-degrees left or right
	//     - otherwise,
	//         - towards the connection's end_edge's starting_node
	// 3. Curved segments that connect the joint between the main and turned bodies
	let arrow_geometry

	const turned_body_length = (turn === TURN_ENUM.through) ? arrowset_linestring_length * ARROW_TURNED_BODY_LENGTH_TO_TOTAL_LENGTH_RATIO
		: TURNING_ARROW_LENGTH_TO_THROUGH_ARROW_LENGTH_RATIO * (arrowset_linestring_length * ARROW_TURNED_BODY_LENGTH_TO_TOTAL_LENGTH_RATIO)

	const main_body_length = (turn === TURN_ENUM.through) ? arrowset_linestring_length - turned_body_length
		: TURNING_ARROW_LENGTH_TO_THROUGH_ARROW_LENGTH_RATIO * (arrowset_linestring_length - turned_body_length)


	const main_body_linestring = lineSliceAlong(arrowset_linestring, 0, main_body_length, { units: 'meters' })
	const main_body_last_segment = [main_body_linestring.geometry.coordinates[main_body_linestring.geometry.coordinates.length - 2], last(main_body_linestring.geometry.coordinates)]

	const main_body_last_segment_bearing = bearing(head(main_body_last_segment), last(main_body_last_segment))

	const turned_body_bearing = (point_towards_coordinates) ? bearing(last(main_body_last_segment), point_towards_coordinates)
		: (turn === TURN_ENUM.through) ? main_body_last_segment_bearing
		: (turn === TURN_ENUM.left) ? main_body_last_segment_bearing - 90
		: (turn === TURN_ENUM.right) ? main_body_last_segment_bearing + 90
		: main_body_last_segment_bearing


	// Determine details of curved segments.
	const turned_body_bearing_relative_to_main_body = turned_body_bearing - main_body_last_segment_bearing
	let degrees_to_turn
	let relative_turn_direction

	switch (true) {
		case (turned_body_bearing_relative_to_main_body === 180 || turned_body_bearing_relative_to_main_body === -180):
			console.error('U-turns currently not supported')
			return
		case (turned_body_bearing_relative_to_main_body >= 0 && turned_body_bearing_relative_to_main_body < 180):
			degrees_to_turn = turned_body_bearing_relative_to_main_body
			relative_turn_direction = 'right'
			break
		case (turned_body_bearing_relative_to_main_body > 180 && turned_body_bearing_relative_to_main_body <= 360):
			degrees_to_turn = 360 - turned_body_bearing_relative_to_main_body
			relative_turn_direction = 'left'
			break
		case (turned_body_bearing_relative_to_main_body < 0 && turned_body_bearing_relative_to_main_body > -180):
			degrees_to_turn = -turned_body_bearing_relative_to_main_body
			relative_turn_direction = 'left'
			break
		case (turned_body_bearing_relative_to_main_body < -180 && turned_body_bearing_relative_to_main_body >= -360):
			degrees_to_turn = 360 + turned_body_bearing_relative_to_main_body
			relative_turn_direction = 'right'
			break
		default:
			console.error('Relative bearing should not be >360 or <-360')
			return
	}

	// Construct arrow, which will eventually trace from right-corner of the arrow's base, then go counter-clockwise, following the geospatial right-hand rule.
	const right_side_of_main_body = lineOffsetPerpendicular(main_body_linestring, offset + (ARROW_TOTAL_WIDTH_METERS / 2) - ARROW_MARGIN_METERS, { units: 'meters' })
	const left_side_of_main_body = lineOffsetPerpendicular(main_body_linestring, offset - (ARROW_TOTAL_WIDTH_METERS / 2) + ARROW_MARGIN_METERS, { units: 'meters' })
	left_side_of_main_body.geometry.coordinates.reverse() // Reversed to preserve arrow polygon's clockwise winding order

	let end_of_right_side_of_turned_body
	let start_of_left_side_of_turned_body
	const right_side_curved_segments = []
	const left_side_curved_segments = []

	// Construct curved segments as arc of radius ARROW_BODY_WIDTH_METERS centered on a respective turn joint.
	if (relative_turn_direction === 'right') {
		end_of_right_side_of_turned_body = transformTranslate(point(last(right_side_of_main_body.geometry.coordinates)), turned_body_length, turned_body_bearing, { units: 'meters' }).geometry.coordinates
		start_of_left_side_of_turned_body = transformTranslate(point(end_of_right_side_of_turned_body), ARROW_BODY_WIDTH_METERS, turned_body_bearing - 90, { units: 'meters' }).geometry.coordinates

		for (let i = 1; i < 16; i++) {
			left_side_curved_segments.push(
				transformTranslate(point(last(right_side_of_main_body.geometry.coordinates)), ARROW_BODY_WIDTH_METERS, ((turned_body_bearing - 90) - (i * degrees_to_turn / 16)), { units: 'meters' }).geometry.coordinates
			)
		}
	} else if (relative_turn_direction === 'left') {
		start_of_left_side_of_turned_body = transformTranslate(point(head(left_side_of_main_body.geometry.coordinates)), turned_body_length, turned_body_bearing, { units: 'meters' }).geometry.coordinates
		end_of_right_side_of_turned_body = transformTranslate(point(start_of_left_side_of_turned_body), ARROW_BODY_WIDTH_METERS, turned_body_bearing + 90, { units: 'meters' }).geometry.coordinates

		for (let i = 1; i < 16; i++) {
			right_side_curved_segments.push(
				transformTranslate(point(head(left_side_of_main_body.geometry.coordinates)), ARROW_BODY_WIDTH_METERS, ((turned_body_bearing + 90) + (i * degrees_to_turn / 16)), { units: 'meters' }).geometry.coordinates
			)
		}
		right_side_curved_segments.reverse() // Reversed to preserve arrow polygon's counter-clockwise winding order
	} else {
		console.error('Invalid relative turn direction')
		return
	}

	const center_of_end_of_turned_body = center(points([end_of_right_side_of_turned_body, start_of_left_side_of_turned_body]))
	const arrow_tip       = transformTranslate(center_of_end_of_turned_body, ARROW_TOTAL_WIDTH_METERS, turned_body_bearing, { units: 'meters' }).geometry.coordinates
	const arrow_left_tip  = transformTranslate(center_of_end_of_turned_body, ARROW_TOTAL_WIDTH_METERS, turned_body_bearing - 90, { units: 'meters' }).geometry.coordinates
	const arrow_right_tip = transformTranslate(center_of_end_of_turned_body, ARROW_TOTAL_WIDTH_METERS, turned_body_bearing + 90, { units: 'meters' }).geometry.coordinates

	arrow_geometry = {
		'type': 'Polygon',
		'coordinates': [
			[
				...right_side_of_main_body.geometry.coordinates,

				...right_side_curved_segments,

				end_of_right_side_of_turned_body,

				arrow_right_tip,
				arrow_tip,
				arrow_left_tip,

				start_of_left_side_of_turned_body,

				...left_side_curved_segments,

				...left_side_of_main_body.geometry.coordinates,
				head(right_side_of_main_body.geometry.coordinates),
			],
		],
	}

	// Add padding between arrow shape and approach bay's stop bar.
	if (should_add_stop_bar_padding) arrow_geometry = transformTranslate(arrow_geometry, ARROWSET_DISTANCE_FROM_STOP_BAR_METERS, main_body_last_segment_bearing, { units: 'meters' })

	// Save main body's offset'd + padded basis line for calculating placement of other UI components, e.g. Markers, labels.
	let main_body_basis_line
	main_body_basis_line = lineOffsetPerpendicular(main_body_linestring, offset, { units: 'meters' })
	if (should_add_stop_bar_padding) main_body_basis_line = transformTranslate(main_body_basis_line, ARROWSET_DISTANCE_FROM_STOP_BAR_METERS, main_body_last_segment_bearing, { units: 'meters' })

	return {
		arrow_geometry,
		main_body_basis_line,
	}

}


export const createArrowsets = ({
	is_for_modelling,
	is_unsignalized,
	edge_connections,
	movements,
	atomic_movements,
	phases,
	signal,
	custom_approach_arrowset_basis_lines,
}) => {

	// An arrowset groups edge_connections under an approach_edge.
	const approach_edges_to_arrowsets = {}

	// 1. Group edge_connections into their respective arrowsets.
	edge_connections.forEach(c => {

		if (!(c?.start_edge_id && c?.start_edge?.gps?.coordinates)) return console.error('createArrowsets: Invalid Connection detected while generating approach arrowset.')
		if (!is_unsignalized && !c?.movement_ids || (!is_for_modelling && !c?.atomic_movement_ids)) return console.error('createArrowsets: Invalid Connection detected while generating signalized approach arrowset - Missing Movement IDs.')

		if (!approach_edges_to_arrowsets[c.start_edge_id]) {

			// Each arrowset's arrowset_linestring will be its spatial basis from which the position of the connection arrows and other related components (e.g. map labels) are determined.
			// If no custom arrowset_linestring exists, derive an arrowset_linestring from the approach edge.
			const custom_arrowset_linestring = custom_approach_arrowset_basis_lines?.find(custom_aabl => custom_aabl?.start_edge_id === c.start_edge_id)?.approach_arrowset_basis_line
			let arrowset_linestring = custom_arrowset_linestring

			if (!arrowset_linestring) {
				// The arrowset_linestring segment goes from the end of the approach edge to a min/max length, contracting or extending from it as needed.
				const arrowset_linestring_length = Math.min(MAX_ARROWSET_LENGTH_METERS, Math.max(MIN_ARROWSET_LENGTH_METERS, length(c.start_edge.gps, { units: 'meters' })))

				// Contract from approach_edge
				if (arrowset_linestring_length <= length(c.start_edge.gps, { units: 'meters' })) {
					arrowset_linestring = lineSliceAlong(
						c.start_edge.gps,
						length(c.start_edge.gps, { units: 'meters' }) - arrowset_linestring_length,
						length(c.start_edge.gps, { units: 'meters' }),
						{ units: 'meters' },
					)
				}
				// Extend from approach_edge
				else {
					// Move the last point along the last and second-to-last points' bearing.
					const last_point = c.start_edge.gps.coordinates[c.start_edge.gps.coordinates.length-1]
					const second_last_point = c.start_edge.gps.coordinates[c.start_edge.gps.coordinates.length-2]

					const geojson_last_point = {
						"type": "Feature",
						"properties": {},
						"geometry": {
							"type": "Point",
							"coordinates": last_point,
						},
					}

					const translate_distance = arrowset_linestring_length - length(c.start_edge.gps, { units: 'meters' })

					const line_extension = transformTranslate(
						geojson_last_point,
						translate_distance,
						bearing(second_last_point, last_point),
						{ units: 'meters' },
					)

					arrowset_linestring = lineString([
						...c.start_edge.gps.coordinates,
						// Extend linestring by adding a new point based on the last segment's bearing.
						line_extension.geometry.coordinates,
					])

				}

			}

			approach_edges_to_arrowsets[c.start_edge_id] = {
				approach_edge:  c.start_edge,
				exit_edge:  c.end_edge,
				arrowset_linestring: arrowset_linestring,
				custom_arrowset_linestring: custom_arrowset_linestring,
				edge_connections: [],
				arrow_records: [],
			}

		}

		approach_edges_to_arrowsets[c.start_edge_id].edge_connections.push(c)

	})

	// 2. Derive arrow records and label positions for each arrowset.
	Object.entries(approach_edges_to_arrowsets).forEach(([approach_edge_id, arrowset]) => {

		arrowset.arrow_records = createArrowsetArrows({
			is_for_modelling,
			is_unsignalized,
			is_custom_arrowset_linestring: (arrowset.custom_arrowset_linestring != null),
			arrowset_linestring: arrowset.arrowset_linestring,
			edge_connections: arrowset.edge_connections,
			movements,
			atomic_movements,
			phases,
			signal_id: signal?.id,
			network_id: signal?.network?.id,
		})

		arrowset.arrowset_labels = createArrowsetLabelPositions({
			arrow_records: arrowset.arrow_records,
		})

		arrowset.volume_labels = createArrowsetVolumeLabelPositions({
			arrow_records: arrowset.arrow_records,
		})

	})


	return approach_edges_to_arrowsets

}

export const createArrowsetArrows = ({
	is_for_modelling,
	is_unsignalized,
	is_custom_arrowset_linestring,
	arrowset_linestring,
	edge_connections,
	movements,
	atomic_movements,
	phases,
	signal_id,
	network_id,
}) => {

	if (
		!arrowset_linestring
		|| !edge_connections
		|| (is_for_modelling && !is_unsignalized && [movements, phases].some(x => x == null))
		|| (!is_for_modelling && !is_unsignalized && [movements, atomic_movements, phases, signal_id, network_id].some(x => x == null))
	) {
		if (!is_for_modelling) {
			console.error('createArrowRecords: Cannot proceed. Incomplete information.')
		}
		return []
	}

	// An arrow record contains the data for a specific start_edge and end_edge.
	// Arrow records are stored in "relative Left-to-Right" order for the approach
	let arrow_records = []

	const arrowset_linestring_length = length(arrowset_linestring, { units: 'meters' })

	// 1. Set up non-gps info of arrow records
	edge_connections.forEach(c => {

		// NOTE: Currently, we do not display u-turns.
		if (c.turn === TURN_ENUM.u) return console.error('createArrowsetsArrows: U-turns currently not supported. Skipping connection.')
		if (!is_unsignalized && !c?.movement_ids || (!is_for_modelling && !c?.atomic_movement_ids)) return console.error('createArrowsetsArrows: Invalid Connection detected while generating signalized approach arrowset - Missing Movement IDs.')

		const movements_for_connection = (movements && Array.isArray(movements)) ? movements.filter(m => c.movement_ids.includes(m.id)) : []
		const atomic_movements_for_connection = (atomic_movements && Array.isArray(atomic_movements)) ? atomic_movements.filter(m => c.atomic_movement_ids.includes(m.id)) : []
		const phase_ids_for_connection = (phases && Array.isArray(phases)) ? phases.filter(p => movements_for_connection.some(m => m.phase_id === p.id) || atomic_movements_for_connection.some(m => m.phase_id === p.id)).map(p => p.id) : []

		const arrow_record_id = `${c.start_edge_id}_${c.end_edge_id}`
		let arrow_record = arrow_records.find(ar => ar.id === arrow_record_id)

		// Update existing record
		if (arrow_record) {
			if (c.turn !== arrow_record.turn || c.start_edge_id !== arrow_record.start_edge_id || c.intersection_id !== arrow_record.intersection_id) return console.error('createArrowsetArrows: Connection is inconsistent with ArrowRecord. Skipping Connection.')
			arrow_record.movement_ids = Array.from(new Set([...arrow_record.movement_ids, ...c.movement_ids]))
			arrow_record.atomic_movement_ids = Array.from(new Set([...arrow_record.atomic_movement_ids, ...c.atomic_movement_ids]))
			arrow_record.phase_ids = Array.from(new Set([...arrow_record.phase_ids, ...phase_ids_for_connection]))
			return
		}


		// Otherwise, create new record

		// Compute convenience values...
		// Would've liked to simply use turfjs's angle() here, but it behaves unexpectedly and does not simply give the clockwise angle from the initial line segment...
		// So we borrow an old calculation here (due to time pressure). Can be cleaned up.
		const approach_edge_start_point = c.start_edge.gps.coordinates[0]
		const approach_edge_end_point = c.start_edge.gps.coordinates[1]

		const approach_edge_bearing = bearing(approach_edge_start_point, approach_edge_end_point)
		const turn_bearing = bearing(approach_edge_end_point, c.end_edge.gps.coordinates[0])

		const turn_bearing_relative_to_approach = turn_bearing - approach_edge_bearing
		let degrees_to_turn
		let relative_turn_direction

		switch (true) {
			case (turn_bearing_relative_to_approach === 180 || turn_bearing_relative_to_approach === -180):
				console.error('U-turns currently not supported')
				return
			case (turn_bearing_relative_to_approach >= 0 && turn_bearing_relative_to_approach < 180):
				degrees_to_turn = turn_bearing_relative_to_approach
				relative_turn_direction = 'right'
				break
			case (turn_bearing_relative_to_approach > 180 && turn_bearing_relative_to_approach <= 360):
				degrees_to_turn = 360 - turn_bearing_relative_to_approach
				relative_turn_direction = 'left'
				break
			case (turn_bearing_relative_to_approach < 0 && turn_bearing_relative_to_approach > -180):
				degrees_to_turn = -turn_bearing_relative_to_approach
				relative_turn_direction = 'left'
				break
			case (turn_bearing_relative_to_approach < -180 && turn_bearing_relative_to_approach >= -360):
				degrees_to_turn = 360 + turn_bearing_relative_to_approach
				relative_turn_direction = 'right'
				break
			default:
				console.error('Relative bearing should not be >360 or <-360')
				return
		}

		arrow_record = {
			arrow_record_id: arrow_record_id,
			edge_connection_id: c.id,
			turn: c.turn,
			start_edge_id: c.start_edge_id,
			end_edge_id: c.end_edge_id,
			start_edge: c.start_edge,
			end_edge: c.end_edge,
			movement_ids: c.movement_ids ?? [],
			atomic_movement_ids: c.atomic_movement_ids ?? [],
			intersection_id: c.intersection_id,
			phase_ids: phase_ids_for_connection,
			signal_id: signal_id,
			network_id: network_id,

			// Only used for sorting; other versions of these will depend on further adjustments and will not be so "simplified"
			simplified_degrees_to_turn: degrees_to_turn,
			simplified_relative_turn_direction: relative_turn_direction,
		}

		arrow_records.push(arrow_record)

	})


	// 2. Sort arrow records into "relative Left-to-Right" order
	arrow_records.sort((a,b) => {

		if (a.turn === TURN_ENUM.u) return -1
		else if (b.turn === TURN_ENUM.u) return 1

		if (a.turn === TURN_ENUM.left && b.turn !== TURN_ENUM.left) return -1
		else if (b.turn === TURN_ENUM.left && a.turn !== TURN_ENUM.left) return 1
		else if (a.turn === TURN_ENUM.through && b.turn === TURN_ENUM.left) return 1
		else if (a.turn === TURN_ENUM.through && b.turn === TURN_ENUM.right) return -1
		else if (b.turn === TURN_ENUM.through && a.turn === TURN_ENUM.left) return -1
		else if (b.turn === TURN_ENUM.through && a.turn === TURN_ENUM.right) return 1
		else if (a.turn === TURN_ENUM.right && b.turn !== TURN_ENUM.right) return 1
		else if (b.turn === TURN_ENUM.right && a.turn !== TURN_ENUM.right) return -1

		// NB: simplified_relative_turn_direction will always be either 'left' or 'right'; mainly useful for comparing a through with another through
		if (a.simplified_relative_turn_direction === 'left' && b.simplified_relative_turn_direction !== 'left') return -1
		else if (b.simplified_relative_turn_direction === 'left' && a.simplified_relative_turn_direction !== 'left') return 1
		else if (a.simplified_relative_turn_direction === 'right' && b.simplified_relative_turn_direction !== 'right') return 1
		else if (b.simplified_relative_turn_direction === 'right' && a.simplified_relative_turn_direction !== 'right') return -1
		else if (a.simplified_relative_turn_direction === 'left' && b.simplified_relative_turn_direction === 'left') return (a.simplified_degrees_to_turn > b.simplified_degrees_to_turn) ? -1 : 1
		else if (a.simplified_relative_turn_direction === 'right' && b.simplified_relative_turn_direction === 'right') return (a.simplified_degrees_to_turn < b.simplified_degrees_to_turn) ? -1 : 1

		return 1

	})


	// 3. Calculate gps coordinates of each arrow record's arrow
	arrow_records.forEach((ar, idx) => {

		const num_gaps_from_center = -(arrow_records.length-1)/2 + (idx-1)
		const arrow_offset = num_gaps_from_center * ARROW_TOTAL_WIDTH_METERS

		const is_sole_connection_for_turn_type = !arrow_records.some(ar2 => ar2.arrow_record_id !== ar.arrow_record_id && ar2.turn === ar.turn)
		const { arrow_geometry, main_body_basis_line } = generateArrowGeometry({
			turn: ar.turn,
			arrowset_linestring,
			offset: arrow_offset,
			point_towards_coordinates: is_sole_connection_for_turn_type ? undefined : head(ar.end_edge.gps.coordinates),
			should_add_stop_bar_padding: !is_custom_arrowset_linestring,
		})

		ar.gps = arrow_geometry
		ar.main_body_basis_line = main_body_basis_line

	})

	return arrow_records

}

export const createArrowsetLabelPositions = ({arrow_records}) => {
	/*
	arrow_records is expected to be an array
	of arrows that all have the same incoming incoming/start edge or
	share the same intersection approach (i.e., westbound, northbound)
	*/
	if (!arrow_records?.length) return []

	const right_turn_arrow_records = arrow_records.filter(ar => ar.turn === TURN_ENUM.right)
	const through_arrow_records = arrow_records.filter(ar => ar.turn === TURN_ENUM.through)
	const left_turn_arrow_records = arrow_records.filter(ar => ar.turn === TURN_ENUM.left)

	/*
    the ordering of the turns in this array
    is intentional, as it ensures labels
    are preferentially drawn from right turn to left turn
    clockwise around the arrowset.
    */
	const arrow_records_by_turn = [
		{
			turn_type: TURN_ENUM.right,
			arrow_records: right_turn_arrow_records,
			label_bearing_adjustment: 90,
		},
		{
			turn_type: TURN_ENUM.through,
			arrow_records: through_arrow_records,
			label_bearing_adjustment: 180,
		},
		{
			turn_type: TURN_ENUM.left,
			arrow_records: left_turn_arrow_records,
			label_bearing_adjustment: -90,
		},
	]

	const label_positions = arrow_records_by_turn.map(turn => {

		const turn_arrow_records = turn.arrow_records
		const label_bearing_adjustment = turn.label_bearing_adjustment

		if (!turn_arrow_records.length) return null

		// NOTE: Currently only supports 1 label per turn type:
		//    - Right turns: Uses rightmost arrow record
		//    - Throughs: Uses center (or center-right through) arrow record
		//    - Left turns: Uses leftmost arrow record
		const arrow_record = (turn.turn_type === TURN_ENUM.right) ? last(turn_arrow_records)
			: (turn.turn_type === TURN_ENUM.left) ? head(turn_arrow_records)
			: turn_arrow_records[Math.floor(turn_arrow_records.length / 2)]

		const main_body_basis_line = arrow_record.main_body_basis_line

		const main_body_coors = main_body_basis_line.geometry.coordinates
		if (main_body_coors.length < 2) return console.error('Insufficient coordinates to place phase label.')

		const first_segment = [main_body_coors[0], main_body_coors[1]]
		const first_segment_bearing = bearing(first_segment[0], first_segment[1])

		const position_point = transformTranslate(point(first_segment[0]), ARROW_TOTAL_WIDTH_METERS * 10, first_segment_bearing + label_bearing_adjustment, { units: 'meters' })

		// Calculate anchor direction from first_segment_bearing
		// Indexed as 45-degree slices where 0th slice is centered at 0-degrees North, i.e. 0th slice goes from at 337.5-degrees North to 22.5-degrees North
		const anchor_direction = 'center' // MARKER_ANCHOR_DIRECTIONS[Math.floor((((first_segment_bearing + 22.5) + 360) % 360) / 45)]

		return {
			id: arrow_record.arrow_record_id,
			geometry: position_point.geometry,
			marker_options: {
				anchor: anchor_direction,
			},
		}
	})

	return label_positions

}

export const createArrowsetVolumeLabelPositions = ({arrow_records}) => {

	const volume_label_positions = arrow_records.map((ar, idx) => {

		const main_body_coors = ar.main_body_basis_line.geometry.coordinates
		if (main_body_coors.length < 2) return console.error('Insufficient coordinates to place volume label.')

		const first_segment = [main_body_coors[0], main_body_coors[1]]
		const first_segment_bearing = bearing(first_segment[0], first_segment[1])

		// Calculate position from first_segment_bearing
		let position_point = transformTranslate(point(first_segment[0]), ARROW_TOTAL_WIDTH_METERS * 3, first_segment_bearing + 180, { units: 'meters' })
		if (arrow_records.length > 1) {
			if (ar.turn == TURN_ENUM.left) {
				position_point = transformTranslate(position_point, ARROW_TOTAL_WIDTH_METERS * 3, first_segment_bearing - 90, { units: 'meters' })
			}
			else if (ar.turn == TURN_ENUM.right) {
				position_point = transformTranslate(position_point, ARROW_TOTAL_WIDTH_METERS * 3, first_segment_bearing + 90, { units: 'meters' })
			}
		}

		// Calculate anchor direction from first_segment_bearing
		// Indexed as 45-degree slices where 0th slice is centered at 0-degrees North, i.e. starts at 337.5-degrees North
		const anchor_direction = 'center' //MARKER_ANCHOR_DIRECTIONS[Math.floor((((first_segment_bearing + 22.5) + 360) % 360) / 45)]

		return {
			id: ar.arrow_record_id,
			geometry: position_point.geometry,
			marker_options: {
				anchor: anchor_direction,
			},
		}
	})

	return volume_label_positions

}

// NOTE: Since an arrow record can be associated with multiple movements, atomic_movements, and phases,
//       we will need to do some more processing to determine the final rendered "display-configured-arrowset"
export const manifestDisplayConfiguredArrowsets = ({
	is_for_modelling,
	is_unsignalized,
	approach_edges_to_arrowsets,

	phase_id,
	approach_id,
	movement_id,
	atomic_movement_id,
	phase_type_id,

	hovered_phase_ids,
	hovered_movement_ids,
	hovered_atomic_movement_ids,
	hovered_edge_connection_ids,

	phases,
	movements_with_connection_counts,
	atomic_movements,
	edge_connections,
	movement_insight_counts,

	phase_metrics,
	phase_metrics_loading,
	movement_metrics,
	movement_metrics_loading,
	atomic_movement_metrics,
	atomic_movement_metrics_loading,
	connection_metrics,
	connection_metrics_loading,
	connection_volumes,
	connection_volumes_loading,
}) => {

	if (!approach_edges_to_arrowsets) return

	const approach_edges_to_display_configured_arrowsets = {}

	Object.entries(approach_edges_to_arrowsets).forEach(([approach_edge_id, arrowset]) => {

		const display_configured_arrowset = {}

		// 1. Determine what each display_arrow will represent.
		//    An arrow record can have multiple entities, e.g. movements and phases, associated with it.
		//    For unsignalized intersections, we simply show an edge connection for a given arrow record,
		//    For signalized intersections, we will need to determine which Movement/Atomic Movement (and so Phase) is "manifested" for the given arrow_record depending on the page's parameters and the following Priority Scale:
		//    	Priority Scale (top is highest priority):
		//    		Any arrow_record.AtomicMovement is hovered
		//    		Any arrow_record.Movement is hovered
		//    		Any arrow_record.Movement.Phase is hovered
		//    		Any arrow_record.AtomicMovement is active
		//    		Any arrow_record.Movement is active
		//    		Any arrow_record.Movement.Phase is active
		//          Any arrow_record.ApproachEdge is active
		//    		arrow_record's manifested Movement's priority is protected > others
		//    		(DISABLED) Any arrow_record.Movement.Phase has alert (health score below 50), lower health scores are higher priority
		//    		arrow_record's manifested Movement has greater number of edge_connections
		display_configured_arrowset.display_configured_arrow_records = arrowset.arrow_records.map(ar => {

			let display_fields = {}

			// Show end_edge name if there's another edge_connection with a same turn type to disambiguate
			const other_same_turn_exists = arrowset.arrow_records.some(ar2 => ar2.id !== ar.id && ar2.turn === ar.turn)
			const end_edge_disambiguation = (other_same_turn_exists) ? ` (to ${ar.end_edge?.road_name}` : ''
			display_fields.edge_connection_name = `${ar?.start_edge?.cardinal_direction ?? getAverageCardinalDirection(ar?.start_edge?.gps, {include_ordinal_directions: true})} ${capitalize(ar?.turn)}${end_edge_disambiguation}`

			// For unsignalized intersections
			if (is_unsignalized) {
				if (approach_id && approach_edge_id !== approach_id) {
					display_fields.is_hidden = true
				}
				display_fields.target_entity_type = 'edge_connection'
				display_fields.target_edge_connection_id = ar.edge_connection_id

				return {
					...ar,
					...display_fields,
				}
			}

			// TEMP: Using phase to color arrow records for now. Probably should be tailored to whatever the arrow records manifest to represent.
			if (phase_metrics_loading) {
				display_fields.is_loading = true
			}


			// Convenience values for hovered cases
			const has_hovered_atomic_movement = hovered_atomic_movement_ids?.some(hovered_am_id => ar.atomic_movement_ids.includes(hovered_am_id))
			const has_hovered_movement = hovered_movement_ids?.some(hovered_m_id => ar.movement_ids.includes(hovered_m_id))
			const has_hovered_phase = hovered_phase_ids?.some(hovered_p_id => ar.phase_ids.includes(hovered_p_id))

			// Convenience values for atomic movement case
			const active_atomic_movement = atomic_movement_id && atomic_movements?.find(am => am.id === atomic_movement_id)
			const parent_movement = active_atomic_movement && movements_with_connection_counts?.find(m => m.id === active_atomic_movement.movement_id)

			// Convenience values for movement case
			const active_movement = movement_id && movements_with_connection_counts?.find(m => m.id === movement_id)


			// Show hovered manifestations above all others (e.g. when user hovers over the entity's bar in a Cross-Section bar chart)
			// but ignore hovered blocks if the movement/parent movement is active for proper navigation set up.
			if (has_hovered_atomic_movement) {
				const first_hovered_atomic_movement_id = hovered_atomic_movement_ids?.find(hovered_am_id => ar.atomic_movement_ids.includes(hovered_am_id))
				const first_hovered_atomic_movement = atomic_movements?.find(am => am.id === first_hovered_atomic_movement_id)
				display_fields.target_entity_type = 'atomic_movement'
				display_fields.target_atomic_movement_id = first_hovered_atomic_movement.id
				display_fields = Object.assign(display_fields, first_hovered_atomic_movement)

				// TEMP - Atomic movements use phase's health score and alert counts
				const metrics_for_phase = phase_metrics?.find(phase_metrics_set => phase_metrics_set.phase_id === first_hovered_atomic_movement.phase_id)
				display_fields.fl_health_score = metrics_for_phase?.fl_health_score ?? 100
			}
			else if (has_hovered_movement) {
				const first_hovered_movement_id = hovered_movement_ids?.find(hovered_m_id => ar.movement_ids.includes(hovered_m_id))
				const first_hovered_movement = movements_with_connection_counts?.find(m => m.id === first_hovered_movement_id)
				if (!phase_id) {
					display_fields.target_entity_type = 'phase'
					display_fields.target_phase_id = first_hovered_movement.phase_id
				}
				else {
					display_fields.target_entity_type = 'movement'
					display_fields.target_movement_id = first_hovered_movement.id
				}
				display_fields = Object.assign(display_fields, first_hovered_movement)

				// TEMP - Movements use phase's health score and alert counts
				const metrics_for_phase = phase_metrics?.find(phase_metrics_set => phase_metrics_set.phase_id === first_hovered_movement.phase_id)
				display_fields.fl_health_score = metrics_for_phase?.fl_health_score ?? 100
			}
			else if (has_hovered_phase) {
				const first_hovered_phase_id = hovered_phase_ids?.find(hovered_p_id => ar.phase_ids.includes(hovered_p_id))
				// For a given phase, an edge connection should theoretically only have one movement activated for it... (e.g. protected-right and permissive-right for the same edge connection should not be simultaneously activated...)
				const ar_movement_for_phase = movements_with_connection_counts
					?.filter(m => m.phase_id === first_hovered_phase_id && ar.movement_ids.includes(m.id))
					?.toSorted((a, b) => {
						if (a.priority === PRIORITY_ENUM.protected && b.priority !== PRIORITY_ENUM.protected) return -1
						if (b.priority === PRIORITY_ENUM.protected && a.priority !== PRIORITY_ENUM.protected) return 1
						if (a.overlap_number == null && b.overlap_number != null) return -1
						if (b.overlap_number == null && a.overlap_number != null) return 1
						if (a?.name && b?.name) return (a.name < b.name) ? -1 : 1
						return (a.overlap_number < b.overlap_number) ? -1 : 1
					})?.[0]
				display_fields = Object.assign(display_fields, ar_movement_for_phase)
				display_fields.target_entity_type = 'phase'
				display_fields.target_phase_id = ar_movement_for_phase.phase_id

				// TEMP - Movements use phase's health score and alert counts
				const metrics_for_phase = phase_metrics?.find(phase_metrics_set => phase_metrics_set.phase_id === ar_movement_for_phase.phase_id)
				display_fields.fl_health_score = metrics_for_phase?.fl_health_score ?? 100
			}
			// Active atomic movement
			else if (atomic_movement_id && ar.atomic_movement_ids.includes(atomic_movement_id)) {
				display_fields.is_active = true
				display_fields = Object.assign(display_fields, active_atomic_movement)

				// TEMP - Atomic movements use phase's health score and alert counts
				const metrics_for_phase = phase_metrics?.find(phase_metrics_set => phase_metrics_set.phase_id === active_atomic_movement.phase_id)
				display_fields.fl_health_score = metrics_for_phase?.fl_health_score ?? 100
			}
			// If there's an active atomic movement, other atomic movements in the parent movement navigate to their atomic movements
			else if (atomic_movement_id && ar.movement_ids.includes(parent_movement.id) && !ar.atomic_movement_ids.includes(atomic_movement_id)) {
				const selectable_atomic_movement = atomic_movements?.find(am => am.movement_id === parent_movement.id && ar.atomic_movement_ids.includes(am.id))
				display_fields.target_entity_type = 'atomic_movement'
				display_fields.target_atomic_movement_id = selectable_atomic_movement.id
				display_fields = Object.assign(display_fields, selectable_atomic_movement)

				// TEMP - Atomic movements use phase's health score and alert counts
				const metrics_for_phase = phase_metrics?.find(phase_metrics_set => phase_metrics_set.phase_id === selectable_atomic_movement.phase_id)
				display_fields.fl_health_score = metrics_for_phase?.fl_health_score ?? 100
			}
			// Active movement and child atomic movements
			else if (movement_id && ar.movement_ids.includes(active_movement?.id)) {
				// If the movement has only one atomic movement in it (i.e. pretty much equivalent to: there is only one edge connection in it), treat it similarly to an active atomic movement
				if (active_movement.num_edge_connections_for_movement === 1) {
					display_fields.is_active = true
					display_fields = Object.assign(display_fields, active_movement)

					// TEMP - Movements use phase's health score and alert counts
					const metrics_for_phase = phase_metrics?.find(phase_metrics_set => phase_metrics_set.phase_id === active_movement.phase_id)
					display_fields.fl_health_score = metrics_for_phase?.fl_health_score ?? 100
				}
				// Otherwise, atomic movements in it navigate to their atomic movements
				else {
					const selectable_atomic_movement = atomic_movements?.find(am => am.movement_id === active_movement.id && ar.atomic_movement_ids.includes(am.id))
					display_fields.target_entity_type = 'atomic_movement'
					display_fields.target_atomic_movement_id = selectable_atomic_movement.id
					display_fields = Object.assign(display_fields, selectable_atomic_movement)

					// TEMP - Atomic movements use phase's health score and alert counts
					const metrics_for_phase = phase_metrics?.find(phase_metrics_set => phase_metrics_set.phase_id === selectable_atomic_movement.phase_id)
					display_fields.fl_health_score = metrics_for_phase?.fl_health_score ?? 100
				}
			}
			// Manifest each remaining arrow's movement based on the Priority Scale,
			// filter those not in a selected phase,
			// and the rest are dimmed and selectable to their movement (if part of the same phase) or phase+movement (if part of a different phase).
			else {
				// "Hide" (severely dim) non-active arrows that are not in a selected approach
				if (approach_id && approach_edge_id !== approach_id) {
					display_fields.is_hidden = true
				}

				const movements_for_ar_with_connection_counts_and_health_scores = movements_with_connection_counts
					.filter(m => ar.movement_ids.includes(m.id))
					.map(m => {
						// TEMP - Atomic movements and Movements use phase's health score and alert counts
						return {
							...m,
							fl_health_score:  phase_metrics?.find(phase_metrics_set => phase_metrics_set.phase_id === m.phase_id)?.fl_health_score ?? 100,
						}
					})
				const atomic_movements_for_ar_with_health_scores = (is_for_modelling) ? []
					: atomic_movements
						.filter(am => ar.atomic_movement_ids.includes(am.id))
						// If a Movement has only one Atomic Movement (e.g. Protected Left), prefer the Movement and filter out the Atomic Movement.
						.filter(am => atomic_movements.filter(other_am => other_am.id !== am.id && other_am.movement_id === am.movement_id).length > 1)
						.map(am => {
							// TEMP - Atomic movements and Movements use phase's health score and alert counts
							return {
								...am,
								fl_health_score: phase_metrics?.find(phase_metrics_set => phase_metrics_set.phase_id === am.phase_id)?.fl_health_score ?? 100,
							}
						})

				const prioritized_movements_and_atomic_movements = [
					...movements_for_ar_with_connection_counts_and_health_scores,
					...atomic_movements_for_ar_with_health_scores.map(m => ({...m, num_edge_connections_for_movement: 1, is_atomic_movement: true}))
				].toSorted((a, b) => {
					if (phase_id && phase_id === a.phase_id && phase_id !== b.phase_id) return -1
					if (phase_id && phase_id === b.phase_id && phase_id !== a.phase_id) return 1
					if (a.priority === PRIORITY_ENUM.protected && b.priority !== PRIORITY_ENUM.protected) return -1
					if (b.priority === PRIORITY_ENUM.protected && a.priority !== PRIORITY_ENUM.protected) return 1
					// if (a.fl_health_score <= 50 && b.fl_health_score > 50) return -1
					// if (a.fl_health_score <= 50 && b.fl_health_score <= 50 && a.fl_health_score < b.fl_health_score) return -1
					if (a.num_edge_connections_for_movement > b.num_edge_connections_for_movement) return -1
					return 1
				})

				const manifested_movement = prioritized_movements_and_atomic_movements[0]
				if (phase_id && manifested_movement?.phase_id !== phase_id) {
					display_fields.is_hidden = true
				}

				if (manifested_movement) {
					const manifested_movement_phase_type_id = (manifested_movement.overlap_number != null) ? `overlap_${manifested_movement.overlap_number}` : manifested_movement.priority

					display_fields = Object.assign(display_fields, manifested_movement)
					if (!phase_id) {
						display_fields.target_entity_type = 'phase'
						display_fields.target_phase_id = manifested_movement.phase_id
					}
					else {
						display_fields.target_entity_type = (manifested_movement.is_atomic_movement) ? 'atomic_movement' : 'movement'
						display_fields[`target_${(manifested_movement.is_atomic_movement) ? 'atomic_movement' : 'movement'}_id`] = manifested_movement.id
					}
					if ((atomic_movement_id && manifested_movement.phase_id !== parent_movement?.phase_id) || (movement_id && manifested_movement.phase_id !== active_movement?.phase_id)) display_fields.target_phase_id = manifested_movement.phase_id
					if (phase_type_id && manifested_movement_phase_type_id !== phase_type_id) display_fields.is_dimmed = true
				}
				else {
					display_fields.is_hidden = true
				}

			}


			const display_phase_id = display_fields.phase_id || display_fields.target_phase_id
			const metrics_for_display_phase = display_phase_id
				? phase_metrics?.find(phase_metrics_set => phase_metrics_set.phase_id === display_phase_id)
				: null

			if (metrics_for_display_phase) {
				display_fields.fl_health_score = metrics_for_display_phase.fl_health_score ?? display_fields.fl_health_score ?? 100
				display_fields.fl_control_delay_per_vehicle_mean_seconds = metrics_for_display_phase.fl_control_delay_per_vehicle_mean_seconds ?? display_fields.fl_control_delay_per_vehicle_mean_seconds
				display_fields.fl_control_delay_per_vehicle_mean_seconds_los = metrics_for_display_phase.fl_control_delay_per_vehicle_mean_seconds_los ?? display_fields.fl_control_delay_per_vehicle_mean_seconds_los
				display_fields.level_of_service = metrics_for_display_phase.fl_control_delay_per_vehicle_mean_seconds_los ?? metrics_for_display_phase.level_of_service ?? display_fields.level_of_service
				display_fields.spillback_ratio = metrics_for_display_phase.spillback_ratio ?? display_fields.spillback_ratio ?? null
			}

			return {
				...ar,
				...display_fields,
			}

		})


		// 2. Update arrowset labels
		display_configured_arrowset.arrowset_labels = manifestArrowsetLabels({
			arrow_records: display_configured_arrowset.display_configured_arrow_records,
			arrowset_labels: arrowset.arrowset_labels,

			phase_id,
			approach_id,
			movement_id,
			atomic_movement_id,
			phase_type_id,

			phases,
			phase_metrics,
			phase_metrics_loading,

			movement_insight_counts
		})

		// 3. Update volume labels for each display_arrow
		display_configured_arrowset.volume_labels = manifestArrowsetVolumeLabels({
			arrow_records: display_configured_arrowset.display_configured_arrow_records,
			volume_labels: arrowset.volume_labels,

			phase_id,
			approach_id,
			movement_id,
			atomic_movement_id,
			phase_type_id,

			connection_volumes,
			connection_volumes_loading,
		})


		approach_edges_to_display_configured_arrowsets[approach_edge_id] = display_configured_arrowset

	})

	return approach_edges_to_display_configured_arrowsets

}

export const manifestArrowsetLabels = ({
	arrow_records,
	arrowset_labels,

	phase_id,
	approach_id,
	movement_id,
	atomic_movement_id,
	phase_type_id,

	phases,
	phase_metrics,
	phase_metrics_loading,

	movement_insight_counts
}) => {
	/*
	arrow_records is expected to be an array
	of arrows that all have the same incoming incoming/start edge or
	share the same intersection approach (i.e., westbound, northbound)
	*/
	if (!arrow_records?.length || !arrowset_labels?.length) return []

	const right_turn_arrow_records = arrow_records.filter(ar => ar.turn === TURN_ENUM.right)
	const through_arrow_records = arrow_records.filter(ar => ar.turn === TURN_ENUM.through)
	const left_turn_arrow_records = arrow_records.filter(ar => ar.turn === TURN_ENUM.left)

	/*
    the ordering of the turns in this array
    is intentional, as it ensures labels
    are preferentially drawn from right turn to left turn
    clockwise around the arrowset.
    */
	const arrow_records_by_turn = [
		{
			turn_type: TURN_ENUM.right,
			arrow_records: right_turn_arrow_records,
		},
		{
			turn_type: TURN_ENUM.through,
			arrow_records: through_arrow_records,
		},
		{
			turn_type: TURN_ENUM.left,
			arrow_records: left_turn_arrow_records,
		},
	]

	const labels = arrow_records_by_turn.map(turn => {

		const turn_arrow_records = turn.arrow_records

		if (!turn_arrow_records.length) return null

		// NOTE: Currently only supports 1 label per turn type:
		//    - Right turns: Uses rightmost arrow record
		//    - Throughs: Uses center (or center-right through) arrow record
		//    - Left turns: Uses leftmost arrow record
		const arrow_record = (turn.turn_type === TURN_ENUM.right) ? last(turn_arrow_records)
			: (turn.turn_type === TURN_ENUM.left) ? head(turn_arrow_records)
			: turn_arrow_records[Math.floor(turn_arrow_records.length / 2)]

		const phase = (arrow_record?.target_phase_id) ? phases?.find(p => p.id === arrow_record.target_phase_id) // When arrow record is a link to a Phase
			: (arrow_record?.phase_id) ? phases?.find(p => p.id === arrow_record.phase_id) // When arrow record only displays information associated with a Phase but is not a link to the Phase (e.g. an arrow within the same Movement)
			: undefined
		const metrics_for_phase = (phase) ? phase_metrics?.find(phase_metrics_set => phase_metrics_set.phase_id === phase.id) : undefined

		const label = arrowset_labels.find(l => l?.id === arrow_record.arrow_record_id)
		if (!label) return console.error('manifestArrowsetLabels: Matching label for arrow record not found.')

		let insight_alert_count = phase?.insight_alert_count
		let insight_state_count = phase?.insight_state_count

		// for phases, we also want to include movements insight alert/state counts
		if (phase && Array.isArray(movement_insight_counts)) {
			for (const record of movement_insight_counts) {
				if (record.phase_id === phase.id) {
					insight_alert_count += record.insight_alert_count
					insight_state_count += record.insight_state_count
				}
			}
		}

		return {
			id: label.id,
			geometry: label.geometry,
			marker_options: label.marker_options,
			insight_alert_count,
			insight_state_count,
			insight_state_reference_date: phase?.insight_state_reference_date,
			is_active: phase_id && arrow_record?.phase_id === phase_id, // Using this instead of ar.is_active, because these labels are currently associated to a Phase, not a specific arrow record.
			is_hidden: arrow_record.is_hidden,
			is_dimmed: arrow_record.is_dimmed,
			is_loading: arrow_record.is_loading || phase_metrics_loading,

			edge_connection_id: arrow_record.edge_connection_id,
			edge_connection_name: arrow_record.edge_connection_name,

			network_id: arrow_record?.network_id,
			signal_id: arrow_record?.signal_id,
			phase_id: phase?.id,
			phase_name: phase?.name,
			phase_number: phase?.number,
			fl_health_score: metrics_for_phase?.fl_health_score ?? 100,
		}
	})

	// Hide duplicates, prefer to show right label, then left label, then center label
	return labels.map((l, idx) => {
		if (l?.phase_name) {
			if (idx === 1 && (l.phase_name === labels[0]?.phase_name || l.phase_name === labels[labels.length-1]?.phase_name)) return null
			if (idx === (labels.length-1) && labels[0] && (l.phase_name === labels[0]?.phase_name || l.phase_name === labels[1]?.phase_name)) return null
		}
		return l
	})

}

export const manifestArrowsetVolumeLabels = ({
	arrow_records,
	volume_labels,

	phase_id,
	approach_id,
	movement_id,
	atomic_movement_id,
	phase_type_id,

	connection_volumes,
	connection_volumes_loading,
}) => {

	if (!arrow_records?.length || !volume_labels?.length) return []

	const labels = arrow_records.map((ar, idx) => {

		const label = volume_labels.find(l => l.id === ar.arrow_record_id)
		if (!label) return console.error('manifestArrowsetLabels: Matching label for arrow record not found.')

		const connection_volume_data = connection_volumes?.find(x => x.connection_id == ar.edge_connection_id)

		return {
			id: label.id,
			geometry: label.geometry,
			marker_options: label.marker_options,

			is_active: ar.is_active,
			is_hidden: ar.is_hidden,
			is_dimmed: ar.is_dimmed,
			is_loading: ar.is_loading || connection_volumes_loading,

			connection_volume_avg: connection_volume_data?.fl_volume_count_per_hour,
			connection_volume_peak: connection_volume_data?.fl_volume_count_per_hour_15_min_peak,
		}
	})

	return labels

}


export const createLaneArrowsets = ({
	edge_with_lanes,
	lane_connections,
	edge_connections,
}) => {


	if (!edge_with_lanes?.lanes?.length) return []


	const arrowset_linestring_length = Math.min(MAX_ARROWSET_LENGTH_METERS, Math.max(MIN_ARROWSET_LENGTH_METERS, length(edge_with_lanes.gps, { units: 'meters' })))

	let arrowset_linestring_along_edge
	if (arrowset_linestring_length <= length(edge_with_lanes.gps, { units: 'meters' })) {
		arrowset_linestring_along_edge = lineSliceAlong(
			edge_with_lanes.gps,
			length(edge_with_lanes.gps, { units: 'meters' }) - arrowset_linestring_length,
			length(edge_with_lanes.gps, { units: 'meters' }),
			{ units: 'meters' },
		)
	}
	else {
		// Move the last point along the last and second-to-last points' bearing.
		const last_point = edge_with_lanes.gps.coordinates[edge_with_lanes.gps.coordinates.length-1]
		const second_last_point = edge_with_lanes.gps.coordinates[edge_with_lanes.gps.coordinates.length-2]

		const geojson_last_point = {
			"type": "Feature",
			"properties": {},
			"geometry": {
				"type": "Point",
				"coordinates": last_point,
			},
		}

		const translate_distance = arrowset_linestring_length - length(edge_with_lanes.gps, { units: 'meters' })

		const line_extension = transformTranslate(
			geojson_last_point,
			translate_distance,
			bearing(second_last_point, last_point),
			{ units: 'meters' },
		)

		arrowset_linestring_along_edge = lineString([
			...edge_with_lanes.gps.coordinates,
			// Extend linestring by adding a new point based on the last segment's bearing.
			line_extension.geometry.coordinates,
		])

	}


	const enriched_edge_connections = edge_connections
		?.map(c => {

			// NOTE: Currently, we do not display u-turns.
			if (c.turn === TURN_ENUM.u) return console.error('createLaneArrows: U-turns currently not supported. Skipping connection.')

			// Compute convenience values...
			// Would've liked to simply use turfjs's angle() here, but it behaves unexpectedly and does not simply give the clockwise angle from the initial line segment...
			// So we borrow an old calculation here (due to time pressure). Can be cleaned up.
			const approach_edge_start_point = c.start_edge.gps.coordinates[0]
			const approach_edge_end_point = c.start_edge.gps.coordinates[1]

			const approach_edge_bearing = bearing(approach_edge_start_point, approach_edge_end_point)
			const turn_bearing = bearing(approach_edge_end_point, c.end_edge.gps.coordinates[0])

			const turn_bearing_relative_to_approach = turn_bearing - approach_edge_bearing
			let degrees_to_turn
			let relative_turn_direction

			switch (true) {
			case (turn_bearing_relative_to_approach === 180 || turn_bearing_relative_to_approach === -180):
				console.error('U-turns currently not supported')
				return
			case (turn_bearing_relative_to_approach >= 0 && turn_bearing_relative_to_approach < 180):
				degrees_to_turn = turn_bearing_relative_to_approach
				relative_turn_direction = 'right'
				break
			case (turn_bearing_relative_to_approach > 180 && turn_bearing_relative_to_approach <= 360):
				degrees_to_turn = 360 - turn_bearing_relative_to_approach
				relative_turn_direction = 'left'
				break
			case (turn_bearing_relative_to_approach < 0 && turn_bearing_relative_to_approach > -180):
				degrees_to_turn = -turn_bearing_relative_to_approach
				relative_turn_direction = 'left'
				break
			case (turn_bearing_relative_to_approach < -180 && turn_bearing_relative_to_approach >= -360):
				degrees_to_turn = 360 + turn_bearing_relative_to_approach
				relative_turn_direction = 'right'
				break
			default:
				console.error('Relative bearing should not be >360 or <-360')
				return
			}

			return {
				...c,
				degrees_to_turn,
				relative_turn_direction,
			}

		})


	const lane_ids_to_arrowsets = {}


	for (const l of edge_with_lanes.lanes) {

		// 1. Calculate offset'd arrowset_linestring_for_lane from arrowset_linestring_along_edge
		const num_gaps_from_center = -(edge_with_lanes.lanes.length-1)/2 + (l.lane_index-1)
		const arrowset_offset = num_gaps_from_center * LANE_ARROWSET_GAP_METERS
		const arrowset_linestring_for_lane = lineOffsetPerpendicular(arrowset_linestring_along_edge, arrowset_offset, { units: 'meters' })
		const offset_edge_linestring_for_lane = lineOffsetPerpendicular(lineString(edge_with_lanes.gps.coordinates), arrowset_offset, { units: 'meters' })

		// 2. Generate metadata for arrow_records for arrowset
		let arrow_records

		const edge_connection_ids_for_lane = lane_connections
			?.filter(lc => lc.from_lane_id === l.id)
			?.map(lc => lc.edge_connection_id)

		// Lane is at an approach edge, so we can derive turn arrows
		if (edge_connection_ids_for_lane?.length && enriched_edge_connections) {

			// Sorted in "relative Left-to-Right" order
			arrow_records = enriched_edge_connections
				.filter(ec => edge_connection_ids_for_lane.includes(ec.id))
				.map(c => ({
					arrow_record_id: `${l.id}_${c.start_edge_id}_${c.end_edge_id}`,
					lane: l,
					lane_id: l.id,
					lane_connection_id: lane_connections?.find(lc => lc.from_lane_id === l.id && lc.edge_connection_id === c.id),
					edge_connection_id: c.id,
					turn: c.turn,
					start_edge_id: c.start_edge_id,
					end_edge_id: c.end_edge_id,
					start_edge: c.start_edge,
					end_edge: c.end_edge,
					intersection_id: c.intersection_id,
					// Only used for sorting; other versions of these will depend on further adjustments and will not be so "simplified"
					simplified_degrees_to_turn: c.degrees_to_turn,
					simplified_relative_turn_direction: c.relative_turn_direction,
				}))
				.sort((a,b) => {

					if (a.turn === TURN_ENUM.u) return -1
					else if (b.turn === TURN_ENUM.u) return 1

					if (a.turn === TURN_ENUM.left && b.turn !== TURN_ENUM.left) return -1
					else if (b.turn === TURN_ENUM.left && a.turn !== TURN_ENUM.left) return 1
					else if (a.turn === TURN_ENUM.through && b.turn === TURN_ENUM.left) return 1
					else if (a.turn === TURN_ENUM.through && b.turn === TURN_ENUM.right) return -1
					else if (b.turn === TURN_ENUM.through && a.turn === TURN_ENUM.left) return -1
					else if (b.turn === TURN_ENUM.through && a.turn === TURN_ENUM.right) return 1
					else if (a.turn === TURN_ENUM.right && b.turn !== TURN_ENUM.right) return 1
					else if (b.turn === TURN_ENUM.right && a.turn !== TURN_ENUM.right) return -1

					// NB: simplified_relative_turn_direction will always be either 'left' or 'right'; mainly useful for comparing a through with another through
					if (a.simplified_relative_turn_direction === 'left' && b.simplified_relative_turn_direction !== 'left') return -1
					else if (b.simplified_relative_turn_direction === 'left' && a.simplified_relative_turn_direction !== 'left') return 1
					else if (a.simplified_relative_turn_direction === 'right' && b.simplified_relative_turn_direction !== 'right') return 1
					else if (b.simplified_relative_turn_direction === 'right' && a.simplified_relative_turn_direction !== 'right') return -1
					else if (a.simplified_relative_turn_direction === 'left' && b.simplified_relative_turn_direction === 'left') return (a.simplified_degrees_to_turn > b.simplified_degrees_to_turn) ? -1 : 1
					else if (a.simplified_relative_turn_direction === 'right' && b.simplified_relative_turn_direction === 'right') return (a.simplified_degrees_to_turn < b.simplified_degrees_to_turn) ? -1 : 1

					return 1

				})
		}
		// Lane is not at an approach edge, so we assume all lanes are throughs
		else {
			arrow_records = [
				{
					arrow_record_id: l.id,
					lane: l,
					lane_id: l.id,
					turn: TURN_ENUM.through,
				}
			]
		}

		// 3. Calculate gps coordinates of each arrow record's arrow
		arrow_records.forEach((ar, idx) => {

			const num_gaps_from_center = -(arrow_records.length-1)/2 + (idx-1)
			const arrow_offset = num_gaps_from_center * ARROW_TOTAL_WIDTH_METERS

			const is_sole_connection_for_turn_type = !arrow_records.some(ar2 => ar2.arrow_record_id !== ar.arrow_record_id && ar2.turn === ar.turn)

			const { arrow_geometry, main_body_basis_line } = generateArrowGeometry({
				turn: ar.turn,
				arrowset_linestring: arrowset_linestring_for_lane,
				offset: arrow_offset,
				point_towards_coordinates: is_sole_connection_for_turn_type ? undefined : head(ar.end_edge.gps.coordinates),
				should_add_stop_bar_padding: true,
			})

			ar.gps = arrow_geometry
			ar.main_body_basis_line = main_body_basis_line

		})


		const arrowset = {
			lane_id: l.id,
			arrowset_linestring: arrowset_linestring_for_lane,
			offset_edge_linestring: offset_edge_linestring_for_lane,
			arrow_records,
		}

		lane_ids_to_arrowsets[l.id] = arrowset

	}

	return lane_ids_to_arrowsets

}
